import PublicLayout from "@/components/layout/PublicLayout";
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { revertEmailChange } from "@/lib/api";
import { track } from "@/lib/analytics";
import { ShieldCheck, AlertTriangle, Lock } from "lucide-react";

// Auto-consumes a 7-day revert token sent to a user's OLD email address
// after their account email was changed. Sister page to /verify-email/:token.
export default function RevertEmailChange() {
    const { token } = useParams();
    const [state, setState] = useState("loading"); // loading | success | error
    const [restored, setRestored] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await revertEmailChange(token);
                if (!cancelled) {
                    setState("success");
                    setRestored(r?.restored_email || null);
                    track("email_change_reverted");
                }
            } catch (err) {
                if (!cancelled) {
                    setError(
                        err?.response?.data?.detail
                            || "This revert link is invalid or has expired.",
                    );
                    setState("error");
                }
            }
        })();
        return () => { cancelled = true; };
    }, [token]);

    return (
        <PublicLayout>
            <section className="bl-paper">
                <div
                    className="bl-container py-20 max-w-md text-center"
                    data-testid="revert-email-page"
                >
                    {state === "loading" && (
                        <>
                            <div className="bl-spinner mx-auto mb-4" />
                            <p className="text-bl-muted">Reverting your account email…</p>
                        </>
                    )}
                    {state === "success" && (
                        <div data-testid="revert-success">
                            <div className="mx-auto w-14 h-14 rounded-full bg-bl-primary/10 flex items-center justify-center">
                                <ShieldCheck className="w-7 h-7 text-bl-primary" />
                            </div>
                            <h1 className="mt-6 font-display font-semibold text-3xl text-bl">
                                Account restored.
                            </h1>
                            <p className="mt-3 text-bl-muted leading-relaxed">
                                Your account email has been reverted to{" "}
                                <span className="text-bl font-medium">
                                    {restored || "your previous address"}
                                </span>
                                .
                            </p>
                            <div className="mt-6 rounded-lg border border-bl bg-bl-cream px-4 py-3 text-left">
                                <div className="flex items-start gap-2">
                                    <Lock className="w-4 h-4 text-bl-primary mt-0.5 shrink-0" />
                                    <p className="text-xs text-bl-muted leading-relaxed">
                                        <span className="font-medium text-bl">
                                            Reset your password now.
                                        </span>{" "}
                                        Whoever changed your email likely had access to your old
                                        password too. A reset locks them out.
                                    </p>
                                </div>
                            </div>
                            <div className="mt-6 flex flex-col gap-2">
                                <Link
                                    to="/forgot-password"
                                    className="bl-btn-primary"
                                    data-testid="revert-success-reset-cta"
                                >
                                    Reset password
                                </Link>
                                <Link
                                    to="/auth/login"
                                    className="text-sm text-bl-muted hover:text-bl"
                                >
                                    Or sign in normally
                                </Link>
                            </div>
                        </div>
                    )}
                    {state === "error" && (
                        <div data-testid="revert-error">
                            <div className="mx-auto w-14 h-14 rounded-full bg-rose-50 border border-rose-200 flex items-center justify-center">
                                <AlertTriangle className="w-7 h-7 text-rose-700" />
                            </div>
                            <h1 className="mt-6 font-display font-semibold text-3xl text-bl">
                                Could not revert.
                            </h1>
                            <p className="mt-3 text-bl-muted leading-relaxed">
                                {error}
                            </p>
                            <p className="mt-6 text-sm text-bl-muted">
                                If you suspect your account was compromised, please{" "}
                                <Link to="/contact" className="text-bl-primary hover:underline">
                                    contact support
                                </Link>{" "}
                                immediately.
                            </p>
                        </div>
                    )}
                </div>
            </section>
        </PublicLayout>
    );
}
